import { addDays } from "date-fns";

import { GRACE_PERIOD_DAYS } from "@/lib/coachHealth/buildCoachLeaderAccessSnapshot";
import { buildCoachHealthEvidenceProfile } from "@/lib/coachHealth/buildCoachHealthEvidenceProfile";
import type {
  CoachHealthEvidenceInput,
  CoachHealthEvidenceProfileItem,
} from "@/lib/coachHealth/coachHealthEvidenceProfileContracts";
import type { CoachHealthEvidenceRouteIdentity } from "@/lib/coachHealth/coachHealthEvidenceRoute";
import {
  fetchCoachHealthEvidenceInput,
  fetchCoachHealthRelationshipAccessSnapshot,
} from "@/lib/coachHealth/fetchCoachHealthEvidenceInput";

export type CoachHealthRelationshipAuthorizationResult =
  | {
      readonly status: "authorized";
      readonly authorized: true;
      readonly nextHardGateAt: string | null;
      readonly relationshipRevision: string;
    }
  | {
      readonly status: "revoked";
      readonly authorized: false;
      readonly nextHardGateAt: string | null;
      readonly relationshipRevision: string | null;
    }
  | {
      readonly status: "retryable_error";
      readonly authorized: false;
      readonly nextHardGateAt: null;
      readonly relationshipRevision: null;
    };

export type CoachHealthEvidenceDestinationAuthorizationResult =
  | {
      readonly status: "authorized";
      readonly item: CoachHealthEvidenceProfileItem;
    }
  | { readonly status: "unavailable" }
  | { readonly status: "retryable_error" };

function rawEvidenceIdentity(evidence: CoachHealthEvidenceInput): {
  sourceId: string;
  leaderId: string;
  relationshipRevision: string;
} | null {
  switch (evidence.sourceType) {
    case "coach_philosophy":
      return null;
    case "completed_coach_call":
      return {
        sourceId: evidence.callId,
        leaderId: evidence.leaderId,
        relationshipRevision: evidence.relationshipRevision,
      };
    case "coach_transcript_session_record":
    case "coach_first_take":
      return {
        sourceId: evidence.recordId,
        leaderId: evidence.leaderId,
        relationshipRevision: evidence.relationshipRevision,
      };
    case "coach_reviewable_transcript":
      return {
        sourceId: evidence.transcriptId,
        leaderId: evidence.leaderId,
        relationshipRevision: evidence.relationshipRevision,
      };
    case "coaching_continuity_moment":
      return {
        sourceId: evidence.momentId,
        leaderId: evidence.leaderId,
        relationshipRevision: evidence.relationshipRevision,
      };
  }
}

export async function getCoachHealthEvidenceDestinationAuthorization({
  coachId,
  identity,
  destinationRoute,
}: {
  readonly coachId: string;
  readonly identity: CoachHealthEvidenceRouteIdentity;
  readonly destinationRoute: string;
}): Promise<CoachHealthEvidenceDestinationAuthorizationResult> {
  try {
    if (identity.leaderId.length === 0 || identity.relationshipRevision.length === 0) {
      return { status: "unavailable" };
    }
    const snapshot = await fetchCoachHealthEvidenceInput({
      authenticatedCoachId: coachId,
      asOf: new Date().toISOString(),
      authorizationLeaderId: identity.leaderId,
    });
    const relationship = snapshot.input.relationships.find(
      (candidate) =>
        candidate.coachId === coachId &&
        candidate.leaderId === identity.leaderId &&
        candidate.relationshipRevision === identity.relationshipRevision &&
        candidate.authorized &&
        candidate.profileResolved &&
        (candidate.accessState === "readable" ||
          candidate.accessState === "grace_period"),
    );
    if (!relationship) return { status: "unavailable" };

    const exactEvidence = snapshot.input.evidence.filter((candidate) => {
      if (candidate.sourceType !== identity.sourceType) return false;
      const rawIdentity = rawEvidenceIdentity(candidate);
      return Boolean(
        rawIdentity &&
        rawIdentity.sourceId === identity.sourceId &&
        rawIdentity.leaderId === identity.leaderId &&
        rawIdentity.relationshipRevision === identity.relationshipRevision,
      );
    });
    if (exactEvidence.length !== 1) return { status: "unavailable" };

    const profile = buildCoachHealthEvidenceProfile({
      ...snapshot.input,
      relationships: [{ ...relationship, lifecycle: "active" }],
      evidence: exactEvidence,
    });
    if (profile.state !== "ready" && profile.state !== "partial") {
      return { status: "unavailable" };
    }

    const item = profile.recordedContextCoverage.evidence.find((candidate) =>
      candidate.sourceType === identity.sourceType &&
      candidate.sourceId === identity.sourceId &&
      candidate.leaderId === identity.leaderId &&
      candidate.relationshipRevision === identity.relationshipRevision &&
      candidate.routeBindingId === identity.routeBindingId &&
      candidate.route === destinationRoute
    );
    return item
      ? { status: "authorized", item }
      : { status: "unavailable" };
  } catch {
    return { status: "retryable_error" };
  }
}

export async function authorizeCoachHealthEvidenceDestination(params: {
  readonly coachId: string;
  readonly identity: CoachHealthEvidenceRouteIdentity;
  readonly destinationRoute: string;
}): Promise<CoachHealthEvidenceProfileItem | null> {
  const result = await getCoachHealthEvidenceDestinationAuthorization(params);
  return result.status === "authorized" ? result.item : null;
}

export async function getCoachHealthRelationshipDestinationAuthorization({
  coachId,
  leaderId,
}: {
  readonly coachId: string;
  readonly leaderId: string;
}): Promise<CoachHealthRelationshipAuthorizationResult> {
  try {
    const snapshot = await fetchCoachHealthRelationshipAccessSnapshot({
      authenticatedCoachId: coachId,
      asOf: new Date().toISOString(),
    });
    const relationship = snapshot.relationships.find(
      (candidate) =>
        candidate.coachId === coachId && candidate.leaderId === leaderId,
    );
    const nextHardGateAt = relationship?.accessState === "grace_period"
      ? addDays(
          new Date(relationship.relationshipStartedAt),
          GRACE_PERIOD_DAYS,
        ).toISOString()
      : null;
    const authorized = Boolean(
      relationship?.authorized &&
        relationship.profileResolved &&
        (relationship.accessState === "readable" ||
          relationship.accessState === "grace_period"),
    );
    if (!authorized || !relationship?.relationshipRevision) {
      return {
        status: "revoked",
        authorized: false,
        nextHardGateAt,
        relationshipRevision: relationship?.relationshipRevision ?? null,
      };
    }
    return {
      status: "authorized",
      authorized: true,
      nextHardGateAt,
      relationshipRevision: relationship.relationshipRevision,
    };
  } catch {
    return {
      status: "retryable_error",
      authorized: false,
      nextHardGateAt: null,
      relationshipRevision: null,
    };
  }
}

export async function authorizeCoachHealthRelationshipDestination(params: {
  readonly coachId: string;
  readonly leaderId: string;
}): Promise<boolean> {
  return (await getCoachHealthRelationshipDestinationAuthorization(params)).authorized;
}
